/* istanbul ignore file */
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Breakpoints } from './Breakpoint';

const BREAKPOINTS: Array<{
  breakpoint: Breakpoints;
  from?: number;
  to?: number;
}> = [
  {
    breakpoint: 'xxxs',
    to: 375,
  },
  {
    breakpoint: 'xxs',
    from: 375,
    to: 480,
  },
  {
    breakpoint: 'xs',
    from: 480,
    to: 600,
  },
  {
    breakpoint: 'sm',
    from: 600,
    to: 840,
  },
  {
    breakpoint: 'md',
    from: 840,
    to: 1080,
  },
  {
    breakpoint: 'lg',
    from: 1080,
    to: 1440,
  },
  {
    breakpoint: 'xlg',
    from: 1440,
    to: 1920,
  },
  {
    breakpoint: 'xxlg',
    from: 1920,
  },
];

const BREAKPOINT_QUERIES = BREAKPOINTS.map(({ breakpoint, from, to }) => ({
  breakpoint,
  // Creating media query for each breakpoint
  query: [
    ...(from ? [`(min-width: ${from + 1}px)`] : []),
    ...(to ? [`(max-width: ${to}px)`] : []),
  ].join(' and '),
}));

const useWatchBreakpoint = (): Breakpoints => {
  if (typeof matchMedia === 'undefined') {
    return 'xxlg';
  }
  // Creating memorized object for optimization
  const matchers = useMemo(
    () =>
      BREAKPOINT_QUERIES.map(({ breakpoint, query }) => ({
        breakpoint,
        matcher: matchMedia(query), // matchMedia browser's method
      })),
    []
  );

  // main function to return matched breakpoint useCallback for optimized memorized function
  const findMatchingBreakpoint = useCallback(() => {
    const foundMatcher = matchers.find(({ matcher }) => matcher.matches);
    return foundMatcher?.breakpoint ?? 'xxlg';
  }, [matchers]);

  const [currentBreakpoint, setBreakpoint] = useState(() =>
    findMatchingBreakpoint()
  );

  const breakpointRef = useRef(currentBreakpoint);
  useEffect(() => {
    breakpointRef.current = currentBreakpoint;
  }, [currentBreakpoint]);

  useEffect(() => {
    // on media change it updates the breakpoint
    const onChange = ({ matches }: MediaQueryListEvent) => {
      if (!matches) {
        return;
      }
      const newBreakpoint = findMatchingBreakpoint();
      if (newBreakpoint !== breakpointRef.current) {
        setBreakpoint(newBreakpoint);
      }
    };
    // adding media listener to onChange so that onChange get called when screen size changes so that it can update the breakpoint
    matchers.forEach(({ matcher }) => matcher.addListener(onChange));
    return () => {
      matchers.forEach(({ matcher }) => matcher.removeListener(onChange));
    };
  }, [matchers, findMatchingBreakpoint, setBreakpoint, breakpointRef]);
  return currentBreakpoint;
};

export default useWatchBreakpoint;
